import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  DollarSign, TrendingUp, FileDown, FileText, Filter,
  ShoppingBag, Bike, UtensilsCrossed, CreditCard, Banknote,
  Calendar, Download, Percent, Receipt
} from 'lucide-react';
import { ExpensesTab } from './ExpensesTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatTHB = (v) => `${(v || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} THB`;

export const AccountingTab = ({ adminRole }) => {
  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('summary');
  const [startDate, setStartDate] = useState(() => {
    const d = new Date(); d.setDate(1);
    return d.toISOString().slice(0, 10);
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [sourceFilter, setSourceFilter] = useState('all');

  // Export dialog state
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportFormat, setExportFormat] = useState('csv');
  const [exportType, setExportType] = useState('summary');
  const [exportIncludeExpenses, setExportIncludeExpenses] = useState(false);
  const [exportStartDate, setExportStartDate] = useState(startDate);
  const [exportEndDate, setExportEndDate] = useState(endDate);
  const [exporting, setExporting] = useState(false);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/accounting/summary`, {
        params: { start_date: startDate, end_date: endDate }
      });
      setSummary(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [startDate, endDate]);

  const fetchTransactions = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/accounting/transactions`, {
        params: { start_date: startDate, end_date: endDate, source: sourceFilter }
      });
      setTransactions(data);
    } catch (e) { console.error(e); }
  }, [startDate, endDate, sourceFilter]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);
  useEffect(() => { if (viewMode === 'detail') fetchTransactions(); }, [viewMode, fetchTransactions]);

  const openExportDialog = (format) => {
    setExportFormat(format);
    setExportStartDate(startDate);
    setExportEndDate(endDate);
    setExportType('summary');
    setExportIncludeExpenses(false);
    setShowExportDialog(true);
  };

  const doExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({
        start_date: exportStartDate,
        end_date: exportEndDate,
        export_type: exportType,
        include_expenses: exportIncludeExpenses.toString(),
      });
      if (exportType === 'detail') params.append('source', sourceFilter);

      const url = `${API}/admin/accounting/export/${exportFormat}?${params.toString()}`;
      const response = await fetch(url);
      const blob = await response.blob();
      const ext = exportFormat === 'csv' ? 'csv' : 'pdf';
      const typeLabel = exportType === 'summary' ? 'resume' : 'detail';
      const filename = `compta_${typeLabel}_${exportStartDate}_${exportEndDate}.${ext}`;

      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(link.href);

      setShowExportDialog(false);
    } catch (e) {
      console.error('Export error:', e);
      alert("Erreur lors de l'export");
    } finally {
      setExporting(false);
    }
  };

  const sourceLabels = {
    dine_in: 'Sur place', takeaway: 'A emporter',
    grab: 'Grab', foodpanda: 'Foodpanda', line_man: 'LINE MAN',
    uber_eats: 'Uber Eats', deliveroo: 'Deliveroo', just_eat: 'Just Eat',
    glovo: 'Glovo', wolt: 'Wolt', bolt_food: 'Bolt Food',
    doordash: 'DoorDash', grubhub: 'Grubhub',
    talabat: 'Talabat', careem: 'Careem', noon_food: 'Noon Food',
    rappi: 'Rappi', ifood: 'iFood', custom_delivery: 'Livraison perso',
  };
  const sourceIcons = { dine_in: UtensilsCrossed, takeaway: ShoppingBag };
  const sourceColors = {
    grab: '#00B14F', foodpanda: '#D70F64', line_man: '#00C300',
    uber_eats: '#222', deliveroo: '#00CCBC', just_eat: '#FF8000',
    glovo: '#FFC244', wolt: '#009DE0', bolt_food: '#34D186',
    doordash: '#FF3008', grubhub: '#F63440',
    talabat: '#FF5800', careem: '#49B44D', noon_food: '#C4A000',
    rappi: '#FF441F', ifood: '#EA1D2C', custom_delivery: '#8B5CF6',
  };
  const paymentLabels = { cash: 'Cash', card_terminal: 'CB Terminal', cb_stripe: 'CB Stripe', google_pay: 'Google Pay', apple_pay: 'Apple Pay' };

  return (
    <div data-testid="accounting-tab">
      {/* Filters */}
      <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3] mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-xs font-medium text-[#6E5E52] mb-1 block">Du</label>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
              className="rounded-xl border-[#E5DCD3] h-9 text-sm w-40" data-testid="accounting-start-date" />
          </div>
          <div>
            <label className="text-xs font-medium text-[#6E5E52] mb-1 block">Au</label>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
              className="rounded-xl border-[#E5DCD3] h-9 text-sm w-40" data-testid="accounting-end-date" />
          </div>
          <div className="flex gap-1.5">
            <button onClick={() => setViewMode('summary')}
              className={`px-3 py-2 rounded-xl text-xs font-medium transition-colors ${viewMode === 'summary' ? 'bg-[#0A422D] text-white' : 'bg-[#F3EDE5] text-[#2D241D]'}`}
              data-testid="view-summary-btn">Resume</button>
            <button onClick={() => setViewMode('detail')}
              className={`px-3 py-2 rounded-xl text-xs font-medium transition-colors ${viewMode === 'detail' ? 'bg-[#0A422D] text-white' : 'bg-[#F3EDE5] text-[#2D241D]'}`}
              data-testid="view-detail-btn">Detail</button>
          </div>
          {adminRole === 'admin' && (
            <div className="flex gap-1.5 ml-auto">
              <Button size="sm" onClick={() => openExportDialog('csv')}
                className="bg-[#2D6A4F] hover:bg-[#1B4332] text-white rounded-xl h-9 text-xs"
                data-testid="export-csv-btn">
                <FileDown className="w-3.5 h-3.5 mr-1" /> CSV
              </Button>
              <Button size="sm" onClick={() => openExportDialog('pdf')}
                className="bg-[#E16833] hover:bg-[#C95A2A] text-white rounded-xl h-9 text-xs"
                data-testid="export-pdf-btn">
                <FileText className="w-3.5 h-3.5 mr-1" /> PDF
              </Button>
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin w-8 h-8 border-4 border-[#D4A853] border-t-transparent rounded-full" />
        </div>
      ) : viewMode === 'summary' && summary ? (
        <SummaryView summary={summary} sourceLabels={sourceLabels} sourceIcons={sourceIcons} sourceColors={sourceColors} paymentLabels={paymentLabels} />
      ) : viewMode === 'detail' ? (
        <DetailView transactions={transactions} sourceFilter={sourceFilter} setSourceFilter={setSourceFilter}
          sourceLabels={sourceLabels} sourceColors={sourceColors} paymentLabels={paymentLabels} />
      ) : null}

      {/* Export Dialog */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent className="max-w-md bg-white rounded-3xl admin-theme">
          <DialogHeader>
            <DialogTitle className="text-[#2D241D] flex items-center gap-2">
              <Download className="w-5 h-5 text-[#D4A853]" />
              Exporter en {exportFormat === 'csv' ? 'CSV' : 'PDF'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5 pt-2">
            {/* Date range */}
            <div>
              <label className="text-sm font-semibold text-[#2D241D] mb-2 block flex items-center gap-1.5">
                <Calendar className="w-4 h-4 text-[#0A422D]" /> Plage de dates
              </label>
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="text-xs text-[#6E5E52] mb-1 block">Du</label>
                  <Input type="date" value={exportStartDate} onChange={(e) => setExportStartDate(e.target.value)}
                    className="rounded-xl border-[#E5DCD3] h-9 text-sm" data-testid="export-start-date" />
                </div>
                <div className="flex-1">
                  <label className="text-xs text-[#6E5E52] mb-1 block">Au</label>
                  <Input type="date" value={exportEndDate} onChange={(e) => setExportEndDate(e.target.value)}
                    className="rounded-xl border-[#E5DCD3] h-9 text-sm" data-testid="export-end-date" />
                </div>
              </div>
              {/* Quick date presets */}
              <div className="flex flex-wrap gap-1.5 mt-2">
                {[
                  { label: "Ce mois", fn: () => { const d = new Date(); d.setDate(1); setExportStartDate(d.toISOString().slice(0,10)); setExportEndDate(new Date().toISOString().slice(0,10)); }},
                  { label: "Mois dernier", fn: () => { const d = new Date(); d.setMonth(d.getMonth()-1, 1); const e = new Date(); e.setDate(0); setExportStartDate(d.toISOString().slice(0,10)); setExportEndDate(e.toISOString().slice(0,10)); }},
                  { label: "Cette semaine", fn: () => { const d = new Date(); const day = d.getDay(); const diff = d.getDate() - day + (day === 0 ? -6 : 1); d.setDate(diff); setExportStartDate(d.toISOString().slice(0,10)); setExportEndDate(new Date().toISOString().slice(0,10)); }},
                  { label: "Tout", fn: () => { setExportStartDate('2024-01-01'); setExportEndDate(new Date().toISOString().slice(0,10)); }},
                ].map(p => (
                  <button key={p.label} onClick={p.fn} type="button"
                    className="px-2.5 py-1 rounded-lg bg-[#F3EDE5] text-[10px] font-medium text-[#6E5E52] hover:bg-[#E5DCD3] transition-colors"
                    data-testid={`preset-${p.label.toLowerCase().replace(/\s/g, '-')}`}>
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Export type */}
            <div>
              <label className="text-sm font-semibold text-[#2D241D] mb-2 block flex items-center gap-1.5">
                <Receipt className="w-4 h-4 text-[#0A422D]" /> Type d'export
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => setExportType('summary')} type="button"
                  className={`p-3 rounded-xl border-2 text-left transition-all ${
                    exportType === 'summary'
                      ? 'border-[#0A422D] bg-[#0A422D]/5'
                      : 'border-[#E5DCD3] hover:border-[#D4A853]'
                  }`}
                  data-testid="export-type-summary">
                  <TrendingUp className={`w-5 h-5 mb-1 ${exportType === 'summary' ? 'text-[#0A422D]' : 'text-[#6E5E52]'}`} />
                  <p className="text-sm font-semibold text-[#2D241D]">Resume</p>
                  <p className="text-[10px] text-[#6E5E52]">KPI, sources, paiements, journalier</p>
                </button>
                <button onClick={() => setExportType('detail')} type="button"
                  className={`p-3 rounded-xl border-2 text-left transition-all ${
                    exportType === 'detail'
                      ? 'border-[#0A422D] bg-[#0A422D]/5'
                      : 'border-[#E5DCD3] hover:border-[#D4A853]'
                  }`}
                  data-testid="export-type-detail">
                  <FileText className={`w-5 h-5 mb-1 ${exportType === 'detail' ? 'text-[#0A422D]' : 'text-[#6E5E52]'}`} />
                  <p className="text-sm font-semibold text-[#2D241D]">Detail</p>
                  <p className="text-[10px] text-[#6E5E52]">Chaque transaction ligne par ligne</p>
                </button>
              </div>
            </div>

            {/* Include expenses */}
            <label className="flex items-center gap-3 p-3 rounded-xl border border-[#E5DCD3] hover:bg-[#F3EDE5] cursor-pointer transition-colors"
              data-testid="export-include-expenses">
              <input type="checkbox" checked={exportIncludeExpenses}
                onChange={(e) => setExportIncludeExpenses(e.target.checked)}
                className="w-4.5 h-4.5 rounded border-[#E5DCD3] text-[#0A422D] focus:ring-[#0A422D]" />
              <div>
                <p className="text-sm font-semibold text-[#2D241D]">Inclure les charges</p>
                <p className="text-[10px] text-[#6E5E52]">Loyer, fournitures, salaires... + benefice net</p>
              </div>
            </label>

            {/* Export button */}
            <Button onClick={doExport} disabled={exporting}
              className={`w-full rounded-xl h-11 font-semibold text-white ${
                exportFormat === 'csv'
                  ? 'bg-[#2D6A4F] hover:bg-[#1B4332]'
                  : 'bg-[#E16833] hover:bg-[#C95A2A]'
              }`}
              data-testid="do-export-btn">
              {exporting ? (
                <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              {exporting ? 'Export en cours...' : `Telecharger ${exportFormat.toUpperCase()}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

function SummaryView({ summary, sourceLabels, sourceIcons, sourceColors, paymentLabels }) {
  const hasExpenses = summary.total_expenses > 0;
  return (
    <div className="space-y-4" data-testid="accounting-summary">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <KPICard icon={DollarSign} label="Chiffre d'affaires" value={formatTHB(summary.total_revenue)} color="#0A422D" />
        <KPICard icon={TrendingUp} label="Marge brute" value={`${formatTHB(summary.total_margin)} (${summary.margin_pct}%)`} color="#2D6A4F" />
        <KPICard icon={Banknote} label="Pourboires" value={formatTHB(summary.total_tips)} color="#D4A853" />
      </div>

      {/* Profitability Section */}
      <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3]" data-testid="profitability-section">
        <h3 className="font-semibold text-[#2D241D] mb-3 flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-[#0A422D]" /> Rentabilite
        </h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="rounded-xl p-3 border border-[#E5DCD3] bg-[#F3EDE5]">
            <span className="text-xs font-medium text-[#6E5E52]">CA</span>
            <p className="text-lg font-bold text-[#0A422D]">{formatTHB(summary.total_revenue)}</p>
          </div>
          <div className="rounded-xl p-3 border border-[#E5DCD3] bg-[#F3EDE5]">
            <span className="text-xs font-medium text-[#6E5E52]">Cout des ventes</span>
            <p className="text-lg font-bold text-[#E16833]">-{formatTHB(summary.total_cost)}</p>
          </div>
          <div className="rounded-xl p-3 border border-[#EF4444]/20 bg-[#EF4444]/5">
            <span className="text-xs font-medium text-[#6E5E52]">Charges</span>
            <p className="text-lg font-bold text-[#EF4444]">-{formatTHB(summary.total_expenses || 0)}</p>
            {hasExpenses && summary.expenses_by_category && (
              <div className="mt-1.5 space-y-0.5">
                {Object.entries(summary.expenses_by_category).sort((a, b) => b[1] - a[1]).slice(0, 4).map(([cat, amt]) => (
                  <div key={cat} className="flex justify-between text-[10px] text-[#6E5E52]">
                    <span>{cat}</span><span>{formatTHB(amt)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className={`rounded-xl p-3 border ${(summary.net_profit || 0) >= 0 ? 'border-[#2D6A4F]/30 bg-[#2D6A4F]/5' : 'border-[#EF4444]/30 bg-[#EF4444]/5'}`}>
            <span className="text-xs font-medium text-[#6E5E52]">Benefice net</span>
            <p className={`text-lg font-bold ${(summary.net_profit || 0) >= 0 ? 'text-[#2D6A4F]' : 'text-[#EF4444]'}`}>
              {formatTHB(summary.net_profit || (summary.total_revenue - summary.total_cost))}
            </p>
            <p className="text-[10px] text-[#6E5E52]">
              Marge nette: {summary.net_margin_pct ?? summary.margin_pct}%
            </p>
          </div>
        </div>
        {summary.total_commissions > 0 && (
          <div className="mt-3 p-3 rounded-xl border border-[#FF8000]/20 bg-[#FF8000]/5" data-testid="commissions-section">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-[#FF8000]">Commissions plateformes</span>
              <span className="text-sm font-bold text-[#FF8000]">-{formatTHB(summary.total_commissions)}</span>
            </div>
            {summary.commission_by_source && Object.entries(summary.commission_by_source).map(([src, amt]) => (
              <div key={src} className="flex justify-between text-[10px] text-[#6E5E52]">
                <span>{sourceLabels[src] || src}</span><span>{formatTHB(amt)}</span>
              </div>
            ))}
            <div className="flex justify-between mt-1 pt-1 border-t border-[#FF8000]/20 text-xs font-medium">
              <span className="text-[#2D241D]">CA net (hors commissions)</span>
              <span className="text-[#2D6A4F]">{formatTHB(summary.net_revenue || summary.total_revenue)}</span>
            </div>
          </div>
        )}
      </div>

      {/* By Source */}
      <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3]">
        <h3 className="font-semibold text-[#2D241D] mb-3 flex items-center gap-2">
          <Filter className="w-4 h-4 text-[#0A422D]" /> Par source
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {Object.entries(summary.by_source).map(([key, value]) => {
            const Icon = sourceIcons[key] || Bike;
            const platformColor = sourceColors[key];
            const isDelivery = !!platformColor;
            return (
              <div key={key} className="rounded-xl p-3 border"
                style={isDelivery ? { borderColor: `${platformColor}40`, backgroundColor: `${platformColor}08` } : { borderColor: '#E5DCD3', backgroundColor: '#F3EDE5' }}>
                <div className="flex items-center gap-2 mb-1">
                  <Icon className="w-4 h-4" style={isDelivery ? { color: platformColor } : { color: '#6E5E52' }} />
                  <span className="text-xs font-medium text-[#6E5E52]">{sourceLabels[key] || key}</span>
                </div>
                <p className="text-lg font-bold text-[#2D241D]">{formatTHB(value)}</p>
                <p className="text-xs text-[#6E5E52]">{summary.order_count[key] || 0} commandes</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* By Payment */}
      <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3]">
        <h3 className="font-semibold text-[#2D241D] mb-3 flex items-center gap-2">
          <CreditCard className="w-4 h-4 text-[#0A422D]" /> Par mode de paiement
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {Object.entries(summary.by_payment).filter(([, v]) => v > 0).map(([key, value]) => (
            <div key={key} className="rounded-xl p-3 border border-[#E5DCD3] bg-[#F3EDE5]">
              <span className="text-xs font-medium text-[#6E5E52]">{paymentLabels[key] || key}</span>
              <p className="text-lg font-bold text-[#2D241D]">{formatTHB(value)}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Daily Table */}
      {summary.daily?.length > 0 && (
        <div className="bg-white rounded-2xl border border-[#E5DCD3] overflow-hidden">
          <div className="px-4 py-3 border-b border-[#E5DCD3]">
            <h3 className="font-semibold text-[#2D241D] flex items-center gap-2">
              <Calendar className="w-4 h-4 text-[#0A422D]" /> Detail journalier
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#F3EDE5] text-left text-xs text-[#6E5E52]">
                  <th className="px-4 py-2">Date</th>
                  <th className="px-4 py-2 text-right">CA</th>
                  <th className="px-4 py-2 text-right">Cout</th>
                  <th className="px-4 py-2 text-right">Marge</th>
                  <th className="px-4 py-2 text-right">Tips</th>
                  <th className="px-4 py-2 text-right">Cmd</th>
                </tr>
              </thead>
              <tbody>
                {summary.daily.map((d) => (
                  <tr key={d.date} className="border-t border-[#F3EDE5] hover:bg-[#FAFAF7]">
                    <td className="px-4 py-2 font-medium text-[#2D241D]">{d.date}</td>
                    <td className="px-4 py-2 text-right text-[#0A422D] font-bold">{formatTHB(d.revenue)}</td>
                    <td className="px-4 py-2 text-right text-[#6E5E52]">{formatTHB(d.cost)}</td>
                    <td className="px-4 py-2 text-right text-[#2D6A4F] font-medium">{formatTHB(d.revenue - d.cost)}</td>
                    <td className="px-4 py-2 text-right text-[#D4A853]">{formatTHB(d.tips)}</td>
                    <td className="px-4 py-2 text-right">{d.orders}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Expenses Section embedded */}
      <div className="mt-6">
        <ExpensesTab embedded={true} />
      </div>
    </div>
  );
}

function DetailView({ transactions, sourceFilter, setSourceFilter, sourceLabels, sourceColors, paymentLabels }) {
  // Get unique sources from transactions
  const activeSources = ['all', ...new Set(transactions.map(t => t.source).filter(Boolean))];

  return (
    <div data-testid="accounting-detail">
      {/* Source filter */}
      <div className="flex gap-1.5 mb-3 overflow-x-auto pb-1 no-scrollbar">
        {activeSources.map(s => {
          const color = sourceColors?.[s];
          return (
            <button key={s} onClick={() => setSourceFilter(s)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                sourceFilter === s ? 'bg-[#0A422D] text-white' : 'bg-white text-[#2D241D] border border-[#E5DCD3]'
              }`}
              style={sourceFilter === s && color ? { backgroundColor: color, borderColor: color } : {}}
              data-testid={`filter-${s}`}>
              {s === 'all' ? 'Tout' : (sourceLabels[s] || s)}
            </button>
          );
        })}
      </div>

      <div className="bg-white rounded-2xl border border-[#E5DCD3] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#F3EDE5] text-left text-xs text-[#6E5E52]">
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2">Heure</th>
                <th className="px-3 py-2">N</th>
                <th className="px-3 py-2">Table</th>
                <th className="px-3 py-2">Source</th>
                <th className="px-3 py-2 text-right">CA</th>
                <th className="px-3 py-2 text-right">Cout</th>
                <th className="px-3 py-2 text-right">Marge</th>
                <th className="px-3 py-2 text-right">Remise</th>
                <th className="px-3 py-2 text-right">Tip</th>
                <th className="px-3 py-2">Paiement</th>
                <th className="px-3 py-2">Serveur</th>
              </tr>
            </thead>
            <tbody>
              {transactions.length === 0 ? (
                <tr><td colSpan={12} className="text-center py-8 text-[#6E5E52]">Aucune transaction</td></tr>
              ) : transactions.map((t, i) => (
                <tr key={i} className="border-t border-[#F3EDE5] hover:bg-[#FAFAF7]">
                  <td className="px-3 py-2 text-[#2D241D]">{t.date}</td>
                  <td className="px-3 py-2 text-[#6E5E52]">{t.time}</td>
                  <td className="px-3 py-2 font-mono text-xs font-semibold text-[#0A422D]">{t.order_number}</td>
                  <td className="px-3 py-2">{t.table}</td>
                  <td className="px-3 py-2">
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold"
                      style={{
                        backgroundColor: `${sourceColors?.[t.source] || (t.source === 'takeaway' ? '#D4A853' : '#0A422D')}15`,
                        color: sourceColors?.[t.source] || (t.source === 'takeaway' ? '#D4A853' : '#0A422D'),
                      }}>
                      {sourceLabels[t.source] || t.source}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right font-bold text-[#0A422D]">{formatTHB(t.revenue)}</td>
                  <td className="px-3 py-2 text-right text-[#6E5E52]">{formatTHB(t.cost)}</td>
                  <td className="px-3 py-2 text-right text-[#2D6A4F]">{formatTHB(t.margin)}</td>
                  <td className="px-3 py-2 text-right text-[#E16833]">{t.discount > 0 ? formatTHB(t.discount) : '-'}</td>
                  <td className="px-3 py-2 text-right text-[#D4A853]">{t.tip > 0 ? formatTHB(t.tip) : '-'}</td>
                  <td className="px-3 py-2 text-xs">{paymentLabels[t.payment_method] || t.payment_method}</td>
                  <td className="px-3 py-2 text-xs text-[#6E5E52]">{t.server}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function KPICard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3]">
      <div className="flex items-center gap-2 mb-1">
        <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${color}15` }}>
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
        <span className="text-xs font-medium text-[#6E5E52]">{label}</span>
      </div>
      <p className="text-lg font-bold text-[#2D241D] mt-1">{value}</p>
    </div>
  );
}
